import type { components } from "../api/contract";
import { request } from "../api/client";
import { setReleaseObserver } from "../api/client";
import { AppError } from "../api/problem";
import { portalReleaseId } from "./identity";
import { updates, type ReleaseDetectionSource } from "./updates.svelte";

export type Release = components["schemas"]["Release"];

export const RELEASE_POLL_INTERVAL_MS = 90_000;
export const RELEASE_POLL_JITTER = 0.2;
export const RELEASE_VISIBLE_STALE_MS = 30_000;
export const RELEASE_BACKOFF_MAX_MS = 15 * 60_000;

export interface ReleaseDetectionRuntime {
  now: () => number;
  random: () => number;
  isVisible: () => boolean;
  setTimeout: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
  clearTimeout: (timer: ReturnType<typeof setTimeout>) => void;
  addVisibilityListener: (listener: () => void) => () => void;
}

export interface ReleaseDetectionOptions {
  localWebRelease?: string;
  requestRelease?: (signal: AbortSignal) => Promise<Release>;
  observe?: (release: Release, source: ReleaseDetectionSource) => void;
  setObserver?: (observer: ((releaseId: string) => void) | null) => void;
  runtime?: Partial<ReleaseDetectionRuntime>;
}

export interface ReleaseDetection {
  readonly running: boolean;
  readonly disabled: boolean;
  readonly failures: number;
  readonly lastCheckedAt: number | null;
  start: () => void;
  stop: () => void;
  checkNow: (source?: Extract<ReleaseDetectionSource, "header" | "poll">) => Promise<void>;
  observeHeader: (releaseId: string) => void;
}

function defaultRuntime(): ReleaseDetectionRuntime {
  return {
    now: () => Date.now(),
    random: () => Math.random(),
    isVisible: () => typeof document === "undefined" || document.visibilityState === "visible",
    setTimeout: (callback, delayMs) => setTimeout(callback, delayMs),
    clearTimeout: (timer) => clearTimeout(timer),
    addVisibilityListener: (listener) => {
      if (typeof document === "undefined") return () => undefined;
      document.addEventListener("visibilitychange", listener);
      return () => document.removeEventListener("visibilitychange", listener);
    }
  };
}

async function getRelease(signal: AbortSignal): Promise<Release> {
  const response = await request<Release>("/v1/release", {
    signal,
    retryOnUnavailable: false
  });
  return response.data;
}

/** Applies bounded symmetric jitter after exponential backoff. */
export function releasePollDelay(failures: number, random: number): number {
  const exponent = Math.max(0, Math.floor(failures) - 1);
  const base = Math.min(RELEASE_POLL_INTERVAL_MS * 2 ** exponent, RELEASE_BACKOFF_MAX_MS);
  const normalizedRandom = Math.max(0, Math.min(1, random));
  const jitter = 1 - RELEASE_POLL_JITTER + normalizedRandom * RELEASE_POLL_JITTER * 2;
  return Math.min(RELEASE_BACKOFF_MAX_MS, Math.round(base * jitter));
}

export function createReleaseDetection(options: ReleaseDetectionOptions = {}): ReleaseDetection {
  const defaults = defaultRuntime();
  const runtime: ReleaseDetectionRuntime = { ...defaults, ...options.runtime };
  const localWebRelease = options.localWebRelease ?? portalReleaseId;
  const requestRelease = options.requestRelease ?? getRelease;
  const observe = options.observe ?? ((release, source) => updates.observe(release, source));
  const setObserver = options.setObserver ?? setReleaseObserver;

  let running = false;
  let disabled = false;
  let failures = 0;
  let lastCheckedAt: number | null = null;
  let nextCheckAt: number | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let removeVisibilityListener: (() => void) | null = null;
  let inFlight: Promise<void> | null = null;
  let controller: AbortController | null = null;
  let lastObservedHeader = localWebRelease;

  function clearTimer(): void {
    if (timer === null) return;
    runtime.clearTimeout(timer);
    timer = null;
  }

  function schedule(delayMs: number): void {
    clearTimer();
    if (!running || disabled) return;
    nextCheckAt = runtime.now() + delayMs;
    if (!runtime.isVisible()) return;
    timer = runtime.setTimeout(() => {
      timer = null;
      void checkNow("poll");
    }, delayMs);
  }

  async function runCheck(source: Extract<ReleaseDetectionSource, "header" | "poll">): Promise<void> {
    clearTimer();
    lastCheckedAt = runtime.now();
    controller = new AbortController();
    try {
      const release = await requestRelease(controller.signal);
      if (!running || disabled) return;
      failures = 0;
      lastObservedHeader = release.web.id;
      observe(release, source);
      schedule(releasePollDelay(0, runtime.random()));
    } catch (error) {
      if (!running || disabled) return;
      if (error instanceof AppError && error.status === 404) {
        disabled = true;
        running = false;
        nextCheckAt = null;
        removeVisibilityListener?.();
        removeVisibilityListener = null;
        setObserver(null);
        clearTimer();
        return;
      }
      if (error instanceof DOMException && error.name === "AbortError") return;
      failures += 1;
      schedule(releasePollDelay(failures, runtime.random()));
    } finally {
      controller = null;
    }
  }

  function checkNow(
    source: Extract<ReleaseDetectionSource, "header" | "poll"> = "poll"
  ): Promise<void> {
    if (!running || disabled) return Promise.resolve();
    if (inFlight !== null) return inFlight;
    inFlight = runCheck(source).finally(() => {
      inFlight = null;
    });
    return inFlight;
  }

  function observeHeader(releaseId: string): void {
    if (!running || disabled || releaseId === localWebRelease || releaseId === lastObservedHeader) return;
    lastObservedHeader = releaseId;
    // `/v1/release` also carries the header. Ignore it while a full-record check is
    // already in flight rather than recursively classifying a poll as a header hit.
    if (inFlight !== null) return;
    void checkNow("header");
  }

  function onVisibilityChange(): void {
    if (!running || disabled) return;
    if (!runtime.isVisible()) {
      clearTimer();
      return;
    }
    const now = runtime.now();
    if (lastCheckedAt === null || now - lastCheckedAt > RELEASE_VISIBLE_STALE_MS) {
      void checkNow("poll");
      return;
    }
    const delay = nextCheckAt === null
      ? releasePollDelay(failures, runtime.random())
      : Math.max(0, nextCheckAt - now);
    schedule(delay);
  }

  function start(): void {
    if (running || disabled) return;
    running = true;
    setObserver(observeHeader);
    removeVisibilityListener = runtime.addVisibilityListener(onVisibilityChange);
    if (runtime.isVisible()) void checkNow("poll");
  }

  function stop(): void {
    if (!running) return;
    running = false;
    clearTimer();
    nextCheckAt = null;
    controller?.abort();
    controller = null;
    removeVisibilityListener?.();
    removeVisibilityListener = null;
    setObserver(null);
  }

  return {
    get running() { return running; },
    get disabled() { return disabled; },
    get failures() { return failures; },
    get lastCheckedAt() { return lastCheckedAt; },
    start,
    stop,
    checkNow,
    observeHeader
  };
}

let installedDetection: ReleaseDetection | null = null;

/** Installs one detector for the lifetime of the browser tab. */
export function installReleaseDetection(): ReleaseDetection {
  installedDetection ??= createReleaseDetection();
  installedDetection.start();
  return installedDetection;
}
